import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, ArrowRight, RefreshCw, TrendingUp, Users, DollarSign, AlertCircle } from 'lucide-react';
import { useAuthStore, UserRole } from '@dukene/shared';

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Countdown timer for OTP expiry
  useEffect(() => {
    if (!expiresAt) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = Math.floor((expiry - now) / 1000);

      if (diff <= 0) {
        setTimeLeft(0);
        setError('OTP has expired. Please request a new one.');
        clearInterval(interval);
      } else {
        setTimeLeft(diff);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  // Resend cooldown timer
  useEffect(() => {
    if (step === 'otp' && !canResend) {
      const timer = setTimeout(() => {
        setCanResend(true);
      }, 60000); // 60 seconds

      return () => clearTimeout(timer);
    }
  }, [step, canResend]);

  // Auto-verify when 6 digits are entered
  useEffect(() => {
    if (otp.length === 6 && !loading && step === 'otp') {
      handleVerifyOTP();
    }
  }, [otp, loading, step]);

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');

    if (cleaned.startsWith('254')) {
      return '+' + cleaned;
    } else if (cleaned.startsWith('0')) {
      return '+254' + cleaned.slice(1);
    } else if (cleaned.length > 0 && !cleaned.startsWith('254')) {
      return '+254' + cleaned;
    }

    return '+' + cleaned;
  };

  const validatePhoneNumber = (phone: string) => {
    const e164Regex = /^\+254[17]\d{8}$/;
    return e164Regex.test(phone);
  };

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const formattedPhone = formatPhoneNumber(phoneNumber);

    if (!validatePhoneNumber(formattedPhone)) {
      setError('Please enter a valid Kenyan phone number (e.g., 0712345678)');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/whatsapp/auth/request-otp-affiliate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formattedPhone,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setExpiresAt(new Date(data.expiresAt));
        setStep('otp');
        setCanResend(false);
      } else {
        setError(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/whatsapp/auth/verify-otp-affiliate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formatPhoneNumber(phoneNumber),
          otp,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Create user object from affiliate data
        const user = {
          id: data.data.affiliate.userId,
          email: data.data.affiliate.user.email,
          firstName: data.data.affiliate.user.firstName,
          lastName: data.data.affiliate.user.lastName,
          role: UserRole.AFFILIATE,
          emailVerified: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Update auth store with user and tokens
        setUser(user, data.data.accessToken, data.data.refreshToken);

        // Navigate to dashboard
        navigate('/');
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setError('');
    setOtp('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/whatsapp/auth/resend-otp-affiliate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formatPhoneNumber(phoneNumber),
        }),
      });

      const data = await response.json();

      if (data.success) {
        setExpiresAt(new Date(data.expiresAt));
        setCanResend(false);
      } else {
        setError(data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Competitive Commission',
      description: 'Earn up to 20% on every sale',
    },
    {
      icon: Users,
      title: 'Real-time Analytics',
      description: 'Track performance instantly',
    },
    {
      icon: DollarSign,
      title: 'Monthly Payouts',
      description: 'Direct account deposits',
    },
  ];

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-gray-100 relative">
        <div className="flex flex-col justify-between w-full p-16 xl:p-24">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black tracking-tight">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium tracking-wide">AFFILIATES</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-16">
            <div>
              <h2 className="text-6xl font-light text-black mb-6 leading-tight tracking-tight">
                {step === 'phone' ? 'Start Earning' : 'Verify Your Number'}
              </h2>
              <div className="w-16 h-0.5 bg-black mb-6"></div>
              <p className="text-lg text-gray-600 leading-relaxed font-light">
                {step === 'phone'
                  ? 'Join our exclusive affiliate program and earn competitive commissions promoting quality products.'
                  : 'Enter the 6-digit code sent to your WhatsApp to continue.'
                }
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="flex items-start gap-6">
                    <div className="w-12 h-12 border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 bg-white">
                      <Icon className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h3 className="text-black font-medium text-base mb-1 tracking-tight">{benefit.title}</h3>
                      <p className="text-gray-500 text-sm font-light">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-8 text-xs text-gray-400 font-light">
            <span>© 2024 Dukene</span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span>All rights reserved</span>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 sm:px-8 lg:px-20 xl:px-32 py-10 sm:py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-12 sm:mb-16">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-black rounded-lg flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-black uppercase tracking-tight">Dukene</h1>
              <p className="text-[10px] text-gray-500 font-medium">AFFILIATES</p>
            </div>
          </div>

          {/* Form Header */}
          <div className="mb-8 sm:mb-12 text-center lg:text-left">
            <h2 className="text-3xl sm:text-4xl font-light text-black mb-2 sm:mb-3 tracking-tight">
              {step === 'phone' ? 'Welcome Back' : 'Enter Code'}
            </h2>
            <p className="text-gray-500 text-sm sm:text-base font-light">
              {step === 'phone'
                ? 'Login with your phone number'
                : `Code sent to your WhatsApp — ${formatPhoneNumber(phoneNumber)}`
              }
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 sm:mb-8 border border-red-100 rounded-xl p-4 bg-red-50/50">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-bold text-red-900 text-xs sm:text-sm mb-0.5">Error</p>
                  <p className="text-xs sm:text-sm text-red-700 font-light">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Phone Step */}
          {step === 'phone' && (
            <form onSubmit={handleRequestOTP} className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-xs sm:text-sm font-bold text-zinc-400 uppercase tracking-widest mb-3 text-center lg:text-left">
                  Phone Number
                </label>
                <div className="flex justify-center lg:justify-start gap-2 sm:gap-3">
                  <select
                    className="w-24 sm:w-32 h-12 px-2 sm:px-3 rounded-xl border border-gray-100 bg-gray-50/50 text-black focus:border-black focus:outline-none focus:ring-1 focus:ring-black text-xs sm:text-sm transition-all font-medium"
                    defaultValue="+254"
                  >
                    <option value="+254">🇰🇪 +254</option>
                  </select>
                  <input
                    id="phone"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="712345678"
                    className="flex-1 h-12 rounded-xl border border-gray-100 bg-gray-50/50 text-black placeholder:text-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black px-4 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    disabled={loading}
                    required
                  />
                </div>
                <p className="text-xs text-gray-400 font-light mt-2 text-center lg:text-left">Enter WhatsApp Number</p>
              </div>

              <button
                type="submit"
                disabled={loading || !phoneNumber}
                className="w-full h-12 bg-black hover:bg-zinc-900 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm uppercase tracking-widest"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* OTP Step */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div>
                <label htmlFor="otp" className="block text-xs sm:text-sm font-bold text-zinc-400 uppercase tracking-widest mb-3 text-center lg:text-left">
                  Verification Code
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full h-14 text-center text-xl sm:text-2xl font-black tracking-[0.5em] sm:tracking-[1em] rounded-xl border border-gray-100 bg-gray-50/50 text-black focus:border-black focus:outline-none focus:ring-1 focus:ring-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                  maxLength={6}
                  required
                />
                {timeLeft > 0 && (
                  <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mt-4 text-center">
                    ⏱ Expires in {formatTime(timeLeft)}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full h-12 bg-black hover:bg-zinc-900 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm uppercase tracking-widest"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    Verify & Login
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-50">
                <button
                  type="button"
                  onClick={() => setStep('phone')}
                  className="text-[10px] font-bold text-zinc-400 hover:text-black uppercase tracking-widest transition-colors"
                >
                  ← Change Number
                </button>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={!canResend || loading}
                  className="text-[10px] font-bold uppercase tracking-widest transition-colors disabled:opacity-30"
                  style={{ color: canResend ? '#000' : '#a1a1aa' }}
                >
                  {canResend ? 'Resend Code' : `Wait ${timeLeft > 0 ? timeLeft : 60}s`}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
